import { ChevronDown, Clock, FileText, Play, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import PracticeTestLayout from '../components/Layout/PracticeTestLayout';
import UpgradePlanPopUP from '../components/ReUsableComponents/UpgradePlanPopUP';
import {
  setPlansPopUpOpen,
  setpopUpPlanMessage
} from '../redux/Slices/PaymentsSlice';
import { setIsStartTest, setUserTestId } from '../redux/Slices/QuestionsSlice';
import { setCompleteTestData } from '../redux/Slices/StartTestSlice/completeTestData';
import {
  ExamPattern,
  Round,
  setNewTemplete
} from '../redux/Slices/StartTestSlice/newTemplete';
import {
  resetTestSettings,
  setCurrentTime,
  setShowInstructions,
  setTestSettings,
  setUserSelectedCompanyTest
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';
import { useGetQuestionBankDataQuery } from '../services/questionBankApi';
import { useLazyGetWrittenTestFullDetailsQuery } from '../services/startTestAPI/resultsTestApi';
import { useGetGenerationalSectionalTestMutation } from '../services/startTestAPI/startTestApi';

type Topic = {
  topicId: string;
  topicName: string;
};

type TestSettings = {
  section: string;
  topics: string[];
  questionsCount: number;
  timeLimit: number;
};

const CreateTestScreen = () => {
  const [selectedSectionName, setSelectedSectionName] = useState<string>('');
  const [questionsCount, setQuestionsCount] = useState<number>(5);
  const [timeLimit, setTimeLimit] = useState<number>(20);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] =
    useState<boolean>(false);
  const isProgrammingQuestion = selectedSectionName === 'Programming Section';
  const { testSettingsState } = useAppSelector(
    state => state.root.startFulltest
  );
  const dispatch = useAppDispatch();
  const { isStartTest } = useAppSelector(state => state.root.questions);
  const { currentData: sectionsData } = useGetQuestionBankDataQuery();
  const [getGenerationalSectionalTest] =
    useGetGenerationalSectionalTestMutation();
  const [fetchFullTestDetails] = useLazyGetWrittenTestFullDetailsQuery();
  // Get topics for selected section
  const availableTopics: Topic[] =
    sectionsData?.data?.sections?.find(
      sec => sec.sectionId === testSettingsState?.sectionId
    )?.topics || [];

  const handleSectionChange = (sectionId: string): void => {
    const res = sectionsData?.data?.sections?.find(
      sec => sec.sectionId === sectionId
    );
    setSelectedSectionName(res?.sectionName || '');
    dispatch(setTestSettings({ sectionId, topicIds: [] }));
    setIsTopicDropdownOpen(false);
  };

  const handleTopicToggle = (topicId: string): void => {
    const updatedTopics = testSettingsState?.topicIds?.includes(topicId)
      ? testSettingsState?.topicIds?.filter(id => id !== topicId)
      : [...(testSettingsState?.topicIds || []), topicId];

    dispatch(setTestSettings({ topicIds: updatedTopics }));
    setErrorMessage('');
  };

  const removeTopic = (topicId: string): void => {
    const updatedTopics = testSettingsState?.topicIds?.filter(
      id => id !== topicId
    );
    dispatch(setTestSettings({ topicIds: updatedTopics }));
  };

  const handleQuestionsCountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (selectedSectionName === 'Programming Section') {
      dispatch(setTestSettings({ numberOfQuestions: 2 }));
      setQuestionsCount(2);
    } else {
      dispatch(setTestSettings({ numberOfQuestions: Number(e.target.value) }));
      setQuestionsCount(Number(e.target.value));
    }
  };

  const handleTimeLimitChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const minutes = Number(e.target.value);
    const seconds = minutes * 60;
    if (selectedSectionName === 'Programming Section') {
      dispatch(setTestSettings({ timeAllotted: 1800 }));
      setTimeLimit(1800);
    } else {
      dispatch(setTestSettings({ timeAllotted: seconds }));
      setTimeLimit(Number(e.target.value));
    }
  };

  useEffect(() => {
    if (selectedSectionName === 'Programming Section') {
      dispatch(setTestSettings({ numberOfQuestions: 2 }));
      dispatch(setTestSettings({ timeAllotted: 1800 }));
    }
    setErrorMessage('');
  }, [selectedSectionName]);

  const handleStartTest = async () => {
    try {
      if (isFormValid) {
        const response =
          await getGenerationalSectionalTest(testSettingsState).unwrap();
        if (response?.status === 'success') {
          const userTestId = response?.data?.id;
          const targetUserId = response?.data?.userId;
          const fullTestResponse = await fetchFullTestDetails({
            userTestId,
            userId: targetUserId
          }).unwrap();
          const fullTestDTO = fullTestResponse?.data;

          const examData: ExamPattern = {
            ...fullTestDTO?.examPatternDTO,
            rounds: fullTestDTO?.examPatternDTO?.rounds?.map(
              (round: Round) => ({
                ...round,
                sections: [...round.sections]?.sort(
                  (a, b) => a?.sectionSortOrder - b?.sectionSortOrder
                )
              })
            )
          };

          dispatch(
            setCompleteTestData([{ ...response?.data, testDTO: fullTestDTO }])
          );
          dispatch(setNewTemplete([examData]));
          dispatch(setIsStartTest(true));
          dispatch(setUserTestId(response.data.id));
          dispatch(setShowInstructions(false));
          dispatch(
            setUserSelectedCompanyTest({
              userId: response?.data?.userId,
              testSetId: fullTestDTO?.id
            })
          );
          dispatch(
            setCurrentTime(examData?.rounds[0]?.sections[0]?.sectionSpanTime)
          );
          setErrorMessage('');
        }
      }
    } catch (error) {
      if ((error as any)?.status === 402) {
        const message =
          (error as any)?.data?.message ||
          'Payment required to start the test.';
        dispatch(setPlansPopUpOpen(true));
        dispatch(setpopUpPlanMessage(message));
      } else {
        const errorMessage =
          (error as any)?.data?.message ||
          'Failed to start test. Please try again.';
        console.error('Error starting test:', errorMessage);
        setErrorMessage(errorMessage);
      }
    }
  };

  const isFormValid: boolean =
    testSettingsState?.sectionId !== '' &&
    testSettingsState?.topicIds?.length > 0;

  const getSelectedTopicNames = (): string[] => {
    return testSettingsState?.topicIds
      ?.map(
        topicId => availableTopics.find(t => t.topicId === topicId)?.topicName
      )
      .filter((name): name is string => Boolean(name));
  };

  const getSelectedSectionName = (): string => {
    return (
      sectionsData?.data?.sections?.find(
        section => section.sectionId === testSettingsState?.sectionId
      )?.sectionName || ''
    );
  };

  const calculateSliderProgress = (
    value: number,
    min: number,
    max: number
  ): number => {
    return ((value - min) / (max - min)) * 100;
  };

  const getTopicTextColor = () => {
    return testSettingsState?.topicIds?.length === 0
      ? 'text-gray-400'
      : 'text-gray-900';
  };

  const getTopicText = (topicIds?: string[]): string => {
    const count = topicIds?.length ?? 0;

    if (count === 0) {
      return 'Select topics';
    }

    const plural = count > 1 ? 's' : '';
    return `${count} topic${plural} selected`;
  };

  useEffect(() => {
    dispatch(resetTestSettings());
  }, []);

  useEffect(() => {
    setSelectedSectionName('');
    setQuestionsCount(5);
    setTimeLimit(20);
    setErrorMessage('');
  }, [isStartTest]);

  return (
    <>
      {isStartTest ? (
        <PracticeTestLayout isOnlyExit={true} />
      ) : (
        <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6'>
          <div className='mx-auto max-w-2xl'>
            <div className='rounded-2xl bg-white p-8 shadow-xl'>
              <div className='mb-8 text-center'>
                <div className='mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100'>
                  <FileText className='h-8 w-8 text-indigo-600' />
                </div>
                <h1 className='mb-2 text-3xl font-bold text-gray-900'>
                  Create Your Own Test
                </h1>
                <p className='text-gray-600'>
                  Customize your test with preferred settings
                </p>
              </div>

              <div className='space-y-6'>
                {/* Section Dropdown */}
                <div>
                  <label className='mb-2 block text-sm font-semibold text-gray-700'>
                    Section <span className='text-red-500'>*</span>
                  </label>
                  <div className='relative'>
                    <select
                      value={testSettingsState?.sectionId || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleSectionChange(e.target.value)
                      }
                      className='w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
                    >
                      <option value=''>Select a section</option>
                      {sectionsData?.data?.sections?.map(section => (
                        <option
                          key={section.sectionId}
                          value={section.sectionId}
                        >
                          {section.sectionName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400' />
                  </div>
                </div>

                {/* Topics Multi-select Dropdown */}
                <div>
                  <label className='mb-2 block text-sm font-semibold text-gray-700'>
                    Topics <span className='text-red-500'>*</span>
                  </label>
                  <div className='relative'>
                    <button
                      type='button'
                      onClick={() =>
                        testSettingsState?.sectionId &&
                        setIsTopicDropdownOpen(!isTopicDropdownOpen)
                      }
                      disabled={!testSettingsState?.sectionId}
                      className={`flex w-full items-center justify-between rounded-lg border border-gray-300 px-4 py-3 text-left ${
                        !testSettingsState?.sectionId
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'bg-white hover:bg-gray-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
                      }`}
                    >
                      <span className={getTopicTextColor()}>
                        {getTopicText(testSettingsState?.topicIds)}
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 text-gray-400 transition-transform ${isTopicDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isTopicDropdownOpen && testSettingsState?.sectionId && (
                      <div className='absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
                        {availableTopics?.map((topic: Topic) => (
                          <label
                            key={topic.topicId}
                            className='flex cursor-pointer items-center px-4 py-2 hover:bg-gray-50'
                          >
                            <input
                              type='checkbox'
                              checked={testSettingsState?.topicIds?.includes(
                                topic?.topicId
                              )}
                              onChange={() => handleTopicToggle(topic?.topicId)}
                              className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                            />
                            <span className='ml-3 text-gray-900'>
                              {topic.topicName}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected Topics Display */}
                  {testSettingsState?.topicIds?.length > 0 && (
                    <div className='mt-2 flex flex-wrap gap-2'>
                      {getSelectedTopicNames().map(
                        (topicName: string, index: number) => (
                          <span
                            key={topicName}
                            className='inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-800'
                          >
                            {topicName}
                            <button
                              type='button'
                              onClick={() =>
                                removeTopic(
                                  testSettingsState?.topicIds?.[index]
                                )
                              }
                              className='ml-2 h-4 w-4 text-indigo-600 hover:text-indigo-800'
                            >
                              <X className='h-3 w-3' />
                            </button>
                          </span>
                        )
                      )}
                    </div>
                  )}
                </div>

                {/* Questions Count Slider */}
                <div>
                  <label className='mb-2 block text-sm font-semibold text-gray-700'>
                    Number of Questions:{' '}
                    <span className='font-bold text-indigo-600'>
                      {isProgrammingQuestion ? '2' : questionsCount}
                    </span>
                  </label>
                  <div className='px-2'>
                    {!isProgrammingQuestion && (
                      <>
                        <input
                          disabled={
                            selectedSectionName === 'Programming Section'
                          }
                          type='range'
                          min='1'
                          max='10'
                          value={questionsCount}
                          onChange={handleQuestionsCountChange}
                          className='slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200'
                          style={{
                            background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${calculateSliderProgress(questionsCount, 1, 10)}%, #e5e7eb ${calculateSliderProgress(questionsCount, 1, 10)}%, #e5e7eb 100%)`
                          }}
                        />
                        <div className='mt-1 flex justify-between text-xs text-gray-500'>
                          <span>1</span>
                          <span>10</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Time Limit Slider */}
                <div>
                  <label className='mb-2 block text-sm font-semibold text-gray-700'>
                    <Clock className='mr-1 inline h-4 w-4' />
                    Time Limit:{' '}
                    <span className='font-bold text-indigo-600'>
                      {isProgrammingQuestion ? '30' : timeLimit} minutes
                    </span>
                  </label>
                  <div className='px-2'>
                    {!isProgrammingQuestion && (
                      <>
                        <input
                          disabled={
                            selectedSectionName === 'Programming Section'
                          }
                          type='range'
                          min='10'
                          max='30'
                          value={timeLimit}
                          onChange={handleTimeLimitChange}
                          className='slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200'
                          style={{
                            background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${calculateSliderProgress(timeLimit, 10, 30)}%, #e5e7eb ${calculateSliderProgress(timeLimit, 10, 30)}%, #e5e7eb 100%)`
                          }}
                        />
                        <div className='mt-1 flex justify-between text-xs text-gray-500'>
                          <span>10 min</span>
                          <span>30 min</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Start Test Button */}
                <div className='pt-6'>
                  <p className='my-3 text-red-500'>{errorMessage}</p>
                  <button
                    onClick={handleStartTest}
                    disabled={!isFormValid}
                    className={`flex w-full items-center justify-center space-x-2 rounded-lg px-6 py-4 text-lg font-semibold transition-all duration-200 ${
                      isFormValid
                        ? 'transform bg-indigo-600 text-white shadow-lg hover:scale-[1.02] hover:bg-indigo-700 hover:shadow-xl'
                        : 'cursor-not-allowed bg-gray-300 text-gray-500'
                    }`}
                  >
                    <Play className='h-5 w-5' />
                    <span>Start Test</span>
                  </button>

                  {!isFormValid && (
                    <p className='mt-2 text-center text-sm text-gray-500'>
                      Please fill all required fields to start the test
                    </p>
                  )}
                </div>
              </div>

              {/* Test Summary */}
              {isFormValid && (
                <div className='mt-6 rounded-lg bg-gray-50 p-4'>
                  <h3 className='mb-2 text-sm font-semibold text-gray-700'>
                    Test Summary:
                  </h3>
                  <div className='space-y-1 text-sm text-gray-600'>
                    <div>
                      Section:{' '}
                      <span className='font-medium'>
                        {getSelectedSectionName()}
                      </span>
                    </div>
                    <div>
                      Topics:{' '}
                      <span className='font-medium'>
                        {getSelectedTopicNames().join(', ')}
                      </span>
                    </div>
                    <div>
                      Questions:{' '}
                      <span className='font-medium'>
                        {isProgrammingQuestion ? '2' : questionsCount}
                      </span>
                    </div>
                    <div>
                      Time Limit:{' '}
                      <span className='font-medium'>
                        {isProgrammingQuestion ? '30' : timeLimit} minutes
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <UpgradePlanPopUP />
        </div>
      )}
    </>
  );
};

export default CreateTestScreen;
